import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SEO from "@/components/SEO";
import { MapPin, Phone, MessageCircle, ArrowLeft, Clock, CheckCircle, Star, Car, Shield, FileText, AlertTriangle, ThumbsUp, Scale, Users, BadgeCheck, XCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Extended area data with unique content
const areaContent = {
  "hamburg": {
    fullName: "Hamburg",
    region: "Hamburg",
    intro: "Als Ihr mobiler Kfz-Gutachter in Hamburg sind wir schnell bei Ihnen – egal ob in der Innenstadt, in den Außenbezirken oder an der Unfallstelle. Mit über 222 zufriedenen Kunden kennen wir die Hansestadt und ihre Besonderheiten.",
    unique: "Hamburg ist als Millionenstadt besonders unfallträchtig. Dichter Verkehr, viele Baustellen und komplexe Kreuzungen führen täglich zu Unfällen. Umso wichtiger ist ein Gutachter, der schnell vor Ort ist und die lokalen Gegebenheiten kennt.",
    localInfo: "Die Hamburger Verkehrssituation mit Elbtunnel, den großen Hauptverkehrsadern wie der B5 und dem Ring 2 sowie den zahlreichen Baustellen der wachsenden Stadt erfordert besondere Aufmerksamkeit. Wir kennen die neuralgischen Punkte der Stadt.",
    districts: ["Hamburg-Mitte", "Hamburg-Nord", "Wandsbek", "Eimsbüttel", "Altona", "Bergedorf", "Harburg"],
    localFaqs: [
      { q: "Wie schnell sind Sie in Hamburg vor Ort?", a: "In Hamburg sind wir in der Regel innerhalb von 30-45 Minuten bei Ihnen. Wir kennen die Stadt und wissen, wie wir auch bei Stau schnell zu Ihnen kommen." },
      { q: "Kommen Sie auch an die Unfallstelle in Hamburg?", a: "Selbstverständlich! Wir begutachten Ihr Fahrzeug direkt am Unfallort, bei Ihnen zu Hause, an Ihrem Arbeitsplatz oder in der Werkstatt – ganz wie es für Sie am praktischsten ist." },
      { q: "Kennen Sie sich mit den Hamburger Versicherungen aus?", a: "Ja, wir arbeiten regelmäßig mit allen in Hamburg tätigen Versicherungen zusammen und kennen deren Abläufe und Anforderungen genau." }
    ]
  },
  "hamburg-mitte": {
    fullName: "Hamburg-Mitte",
    region: "Hamburg",
    intro: "Im Herzen Hamburgs sind wir Ihr zuverlässiger Kfz-Gutachter. Von der HafenCity bis St. Georg, von der Speicherstadt bis zum Hauptbahnhof – wir sind schnell bei Ihnen.",
    unique: "Hamburg-Mitte mit seinen engen Straßen, Touristenverkehr und der historischen Bebauung stellt besondere Anforderungen an Gutachter. Wir kennen die Gegebenheiten vor Ort.",
    localInfo: "Der Bezirk Hamburg-Mitte umfasst nicht nur die Innenstadt, sondern auch Stadtteile wie St. Pauli, Hammerbrook und Rothenburgsort. Besonders in der Innenstadt und rund um den Hauptbahnhof ist das Verkehrsaufkommen hoch.",
    localFaqs: [
      { q: "Gibt es Parkmöglichkeiten für die Begutachtung in der Innenstadt?", a: "Wir kommen zu Ihnen – Sie müssen sich um nichts kümmern. Alternativ können wir uns auch an einem Ort mit besseren Parkmöglichkeiten treffen." },
      { q: "Wie läuft ein Gutachten in der HafenCity ab?", a: "In der HafenCity begutachten wir Ihr Fahrzeug gerne in einer der Tiefgaragen oder an einem vereinbarten Treffpunkt. Wir sind flexibel und richten uns nach Ihnen." }
    ]
  },
  "hamburg-nord": {
    fullName: "Hamburg-Nord",
    region: "Hamburg",
    intro: "In Hamburg-Nord sind wir Ihr kompetenter Ansprechpartner für Kfz-Gutachten. Von Winterhude bis Barmbek, von Eppendorf bis Fuhlsbüttel – wir kommen zu Ihnen.",
    unique: "Hamburg-Nord zeichnet sich durch eine Mischung aus Wohngebieten und Durchgangsstraßen aus. Besonders an den großen Kreuzungen und Hauptverkehrsadern kommt es häufig zu Unfällen.",
    localInfo: "Die beliebten Wohnviertel Winterhude und Eppendorf haben viel Parkdruck und enge Straßen. Entlang der Alster und an den großen Kreuzungen wie Hudtwalckerstraße oder Mundsburg passieren regelmäßig Unfälle.",
    localFaqs: [
      { q: "Sind Sie auch in Winterhude und Eppendorf tätig?", a: "Ja, gerade in den beliebten Stadtteilen Winterhude und Eppendorf sind wir häufig im Einsatz. Wir kennen die Parkplatzsituation und kommen direkt zu Ihnen." },
      { q: "Wie schnell können Sie nach Fuhlsbüttel kommen?", a: "Nach Fuhlsbüttel sind wir in der Regel innerhalb von 30-40 Minuten bei Ihnen, je nach Verkehrslage auch schneller." }
    ]
  },
  "wandsbek": {
    fullName: "Wandsbek",
    region: "Hamburg",
    intro: "In Wandsbek und Umgebung sind wir Ihr erfahrener Kfz-Sachverständiger. Schnell vor Ort, professionell in der Begutachtung und immer auf Ihrer Seite.",
    unique: "Wandsbek als einwohnerstärkster Bezirk Hamburgs verzeichnet entsprechend viele Verkehrsunfälle. Wir kennen die neuralgischen Punkte und sind schnell zur Stelle.",
    localInfo: "Von Wandsbek-Gartenstadt bis Rahlstedt, von Tonndorf bis Jenfeld – der Bezirk Wandsbek ist vielfältig. Die großen Einkaufszentren und die Anbindung an die A1 sorgen für hohes Verkehrsaufkommen.",
    districts: ["Wandsbek-Gartenstadt", "Rahlstedt", "Tonndorf", "Jenfeld", "Farmsen-Berne"],
    localFaqs: [
      { q: "Kommen Sie auch nach Rahlstedt?", a: "Selbstverständlich! Rahlstedt und alle anderen Stadtteile von Wandsbek gehören zu unserem Einsatzgebiet. Wir sind meist innerhalb von 45 Minuten bei Ihnen." },
      { q: "Gibt es besondere Unfallschwerpunkte in Wandsbek?", a: "Ja, besonders die Wandsbeker Chaussee, die Kreuzung am Wandsbeker Markt und die Zufahrten zur A1 sind unfallträchtig. Wir kennen diese Stellen gut." }
    ]
  },
  "eimsbuettel": {
    fullName: "Eimsbüttel",
    region: "Hamburg",
    intro: "In Eimsbüttel und den angrenzenden Stadtteilen sind wir für Sie da. Ob Hoheluft, Stellingen oder Lokstedt – wir sind Ihr Kfz-Gutachter vor Ort.",
    unique: "Eimsbüttel mit seinen beliebten Wohnvierteln und dem entsprechend hohen Parkdruck ist bekannt für Parkunfälle und kleinere Kollisionen.",
    localInfo: "Die dichte Bebauung in Eimsbüttel, Hoheluft und Stellingen führt zu engem Parken und entsprechenden Schäden. Auch der Verkehr rund um das UKE und den Hagenbecks Tierpark ist oft dicht.",
    localFaqs: [
      { q: "Begutachten Sie auch Parkschäden?", a: "Ja, gerade in Eimsbüttel sind Parkschäden häufig. Wir dokumentieren auch kleinere Schäden professionell, damit Sie Ihre Ansprüche geltend machen können." },
      { q: "Wie läuft die Begutachtung in Hoheluft ab?", a: "Wir kommen zu Ihnen nach Hause oder an einen vereinbarten Treffpunkt. Die Begutachtung dauert etwa 30-45 Minuten, das Gutachten erhalten Sie innerhalb von 24 Stunden." }
    ]
  },
  "altona": {
    fullName: "Altona",
    region: "Hamburg",
    intro: "Von Ottensen bis Blankenese, von Bahrenfeld bis zur Elbchaussee – in Altona sind wir Ihr zuverlässiger Partner für professionelle Kfz-Gutachten.",
    unique: "Altona vereint städtisches Flair mit den vornehmen Elbvororten. Die unterschiedlichen Verkehrssituationen erfordern einen erfahrenen Gutachter.",
    localInfo: "Der Bezirk Altona reicht von den quirligen Straßen Ottensens bis zu den ruhigen Villenvierteln in Blankenese. Die Elbchaussee und die Große Bergstraße sind bekannte Unfallstrecken.",
    districts: ["Ottensen", "Bahrenfeld", "Blankenese", "Rissen", "Osdorf"],
    localFaqs: [
      { q: "Kommen Sie auch nach Blankenese?", a: "Ja, wir sind in ganz Altona tätig – von Ottensen bis Blankenese. Gerade in den Elbvororten mit ihren kurvigen Straßen sind wir regelmäßig im Einsatz." },
      { q: "Wie verhält es sich mit Gutachten für hochwertige Fahrzeuge?", a: "Wir haben Erfahrung mit Fahrzeugen aller Preisklassen. Gerade bei hochwertigen Fahrzeugen ist ein unabhängiges Gutachten besonders wichtig, um die volle Entschädigung zu erhalten." }
    ]
  },
  "bergedorf": {
    fullName: "Bergedorf",
    region: "Hamburg",
    intro: "Im Hamburger Osten sind wir Ihr Kfz-Sachverständiger. Von Bergedorf-Stadt bis zu den Vier- und Marschlanden – wir kommen zu Ihnen.",
    unique: "Bergedorf als eigenständige kleine Stadt im Osten Hamburgs hat sein eigenes Verkehrsaufkommen. Die Anbindung an die A1 sorgt für entsprechenden Durchgangsverkehr.",
    localInfo: "Bergedorf verbindet städtischen Charakter mit ländlicher Umgebung. Die B5 und die Anbindung zur A1/A25 sind stark befahren. In den Vier- und Marschlanden kommt es häufig zu Wildunfällen.",
    localFaqs: [
      { q: "Sind Sie auch in den Vier- und Marschlanden tätig?", a: "Ja, wir kommen auch in die ländlicheren Gebiete wie Kirchwerder, Neuengamme oder Altengamme. Gerade hier sind wir bei Wildunfällen oft gefragt." },
      { q: "Wie sieht es mit Unfällen auf der A1 aus?", a: "Bei Unfällen auf der A1 können wir nach Abschluss der polizeilichen Maßnahmen das Fahrzeug begutachten – entweder am Abschleppziel oder beim Abschleppdienst." }
    ]
  },
  "billstedt": {
    fullName: "Billstedt",
    region: "Hamburg",
    intro: "In Billstedt und Umgebung sind wir für Sie da. Schnelle Terminvergabe, professionelle Begutachtung und faire Abwicklung mit der Versicherung.",
    unique: "Billstedt als einer der einwohnerstärksten Stadtteile Hamburgs hat ein entsprechend hohes Verkehrsaufkommen. Die Nähe zur A1 führt zu regelmäßigen Unfällen.",
    localInfo: "Die Billstedter Hauptstraße und die Möllner Landstraße sind stark befahren. Der Stadtteil wächst, und mit ihm das Verkehrsaufkommen.",
    localFaqs: [
      { q: "Wie schnell können Sie nach Billstedt kommen?", a: "Nach Billstedt sind wir meist innerhalb von 40 Minuten bei Ihnen. Bei dringenden Fällen versuchen wir, noch schneller zu sein." },
      { q: "Kennen Sie sich mit Unfällen an der Möllner Landstraße aus?", a: "Ja, die Möllner Landstraße ist uns bestens bekannt. Wir haben hier bereits viele Unfälle begutachtet und kennen die typischen Schadensmuster." }
    ]
  },
  "langenhorn": {
    fullName: "Langenhorn",
    region: "Hamburg",
    intro: "In Langenhorn und dem Hamburger Norden sind wir Ihr kompetenter Ansprechpartner für alle Fragen rund um Kfz-Gutachten.",
    unique: "Langenhorn liegt strategisch günstig an der Grenze zu Schleswig-Holstein. Viele Pendler passieren hier täglich die Stadtgrenze.",
    localInfo: "Die Langenhorner Chaussee und die Tangstedter Landstraße verbinden Hamburg mit dem Umland. Das Einkaufszentrum Langenhorn Markt und das Krankenhaus Heidberg erzeugen zusätzlichen Verkehr.",
    localFaqs: [
      { q: "Sind Sie auch im Hamburger Norden außerhalb von Langenhorn tätig?", a: "Ja, wir sind auch in Fuhlsbüttel, Hummelsbüttel und Ohlsdorf im Einsatz. Der gesamte Hamburger Norden gehört zu unserem Einzugsgebiet." },
      { q: "Wie verhält es sich mit Pendlern aus Schleswig-Holstein?", a: "Viele unserer Kunden pendeln zwischen Hamburg und Schleswig-Holstein. Bei einem Unfall auf dem Weg sind wir schnell zur Stelle – egal auf welcher Seite der Landesgrenze." }
    ]
  },
  "harburg": {
    fullName: "Harburg",
    region: "Hamburg",
    intro: "In Hamburg-Harburg südlich der Elbe sind wir für Sie da. Professionelle Kfz-Gutachten für faire Anspruchsdurchsetzung.",
    unique: "Harburg als Hamburger Bezirk südlich der Elbe hat eigene Verkehrscharakteristiken. Die Elbtunnel-Anbindung sorgt für hohes Verkehrsaufkommen.",
    localInfo: "Der Bezirk Harburg umfasst neben der Harburger Innenstadt auch Wilhelmsburg, die Elbinsel und ländliche Gebiete bis an die Landesgrenze. Die A7 und die A1 prägen das Verkehrsgeschehen.",
    districts: ["Harburg-Stadt", "Wilhelmsburg", "Hausbruch", "Neugraben-Fischbek"],
    localFaqs: [
      { q: "Kommen Sie auch nach Wilhelmsburg?", a: "Ja, Wilhelmsburg und alle anderen Stadtteile von Harburg gehören zu unserem Einsatzgebiet. Wir sind auch auf der Elbinsel schnell bei Ihnen." },
      { q: "Wie sieht es mit Unfällen im Elbtunnel aus?", a: "Bei Unfällen im Elbtunnel wird das Fahrzeug in der Regel aus dem Tunnel geschleppt. Wir können dann das Fahrzeug beim Abschleppdienst oder an einem anderen vereinbarten Ort begutachten." }
    ]
  },
  "schleswig-holstein": {
    fullName: "Schleswig-Holstein",
    region: "Schleswig-Holstein",
    intro: "Als Ihr Kfz-Gutachter in Schleswig-Holstein sind wir im gesamten südlichen Landesteil für Sie unterwegs. Von Pinneberg bis Elmshorn, von Quickborn bis Wedel.",
    unique: "Schleswig-Holstein mit seinen Landstraßen und der Nähe zu Hamburg erfordert einen mobilen Gutachter, der flexibel und schnell reagieren kann.",
    localInfo: "Das südliche Schleswig-Holstein ist eng mit Hamburg verflochten. Viele Menschen pendeln täglich zwischen den Kreisen Pinneberg, Segeberg und Steinburg und der Hansestadt.",
    districts: ["Pinneberg", "Elmshorn", "Itzehoe", "Quickborn", "Wedel", "Schenefeld", "Halstenbek"],
    localFaqs: [
      { q: "Wie weit fahren Sie in Schleswig-Holstein?", a: "Unser Haupteinsatzgebiet ist das südliche Schleswig-Holstein, insbesondere die Kreise Pinneberg und Steinburg. Bei Bedarf kommen wir aber auch weiter nördlich zu Ihnen." },
      { q: "Kennen Sie sich mit ländlichen Unfällen aus?", a: "Ja, Unfälle auf Landstraßen haben oft andere Charakteristiken als städtische Unfälle. Wir haben Erfahrung mit beiden Situationen und passen unsere Begutachtung entsprechend an." },
      { q: "Wie verhält es sich mit Wildunfällen?", a: "Wildunfälle sind auf den Landstraßen Schleswig-Holsteins leider häufig. Wir dokumentieren diese Schäden fachgerecht für Ihre Kaskoversicherung." }
    ]
  },
  "pinneberg": {
    fullName: "Pinneberg",
    region: "Schleswig-Holstein",
    intro: "In Pinneberg und dem Kreis Pinneberg sind wir Ihr erfahrener Kfz-Sachverständiger. Schnell vor Ort und professionell in der Abwicklung.",
    unique: "Der Kreis Pinneberg als unmittelbarer Nachbar Hamburgs verzeichnet viel Pendlerverkehr. Die Autobahn A23 und die Bundesstraßen sorgen für entsprechendes Unfallgeschehen.",
    localInfo: "Pinneberg als Kreisstadt ist Verwaltungs- und Wirtschaftszentrum. Die gute Anbindung an Hamburg über die A23 und die S-Bahn macht die Stadt attraktiv, führt aber auch zu hohem Verkehrsaufkommen.",
    localFaqs: [
      { q: "Wie schnell sind Sie in Pinneberg?", a: "Nach Pinneberg sind wir in der Regel innerhalb von 45-60 Minuten bei Ihnen. Bei dringenden Fällen versuchen wir, noch schneller zu sein." },
      { q: "Kennen Sie die Versicherungen im Kreis Pinneberg?", a: "Wir arbeiten regelmäßig mit Versicherungen zusammen, die im Kreis Pinneberg tätig sind. Die Abläufe sind uns vertraut." }
    ]
  },
  "elmshorn": {
    fullName: "Elmshorn",
    region: "Schleswig-Holstein",
    intro: "In Elmshorn und Umgebung sind wir für Sie da. Als größte Stadt im Kreis Pinneberg hat Elmshorn entsprechenden Bedarf an professionellen Kfz-Gutachten.",
    unique: "Elmshorn als Mittelzentrum in Schleswig-Holstein ist Knotenpunkt mehrerer Verkehrsadern. Die Nähe zu Hamburg sorgt für hohes Verkehrsaufkommen.",
    localInfo: "Die Innenstadt von Elmshorn, die Bundesstraße 431 und die Anbindung zur A23 sind stark frequentiert. Elmshorn wächst, und mit der Stadt auch der Verkehr.",
    localFaqs: [
      { q: "Sind Sie regelmäßig in Elmshorn tätig?", a: "Ja, Elmshorn gehört zu unseren häufigen Einsatzorten. Wir kennen die Stadt und ihre Verkehrssituation gut." },
      { q: "Wie läuft die Abwicklung mit Elmshorner Werkstätten?", a: "Wir arbeiten mit mehreren Werkstätten in Elmshorn zusammen und können Ihr Fahrzeug auch dort begutachten, wenn Sie das wünschen." }
    ]
  },
  "itzehoe": {
    fullName: "Itzehoe",
    region: "Schleswig-Holstein",
    intro: "In Itzehoe und dem Kreis Steinburg sind wir Ihr kompetenter Ansprechpartner für Kfz-Gutachten aller Art.",
    unique: "Itzehoe als Kreisstadt verbindet ländliche Gebiete mit städtischem Verkehr. Die Bundesstraßen und die Nähe zur A23 prägen das Verkehrsgeschehen.",
    localInfo: "Itzehoe ist das Zentrum des Kreises Steinburg. Die Stör durchquert die Stadt, mehrere Bundesstraßen kreuzen sich hier. Das führt zu einem komplexen Verkehrsgeschehen.",
    localFaqs: [
      { q: "Kommen Sie auch nach Itzehoe?", a: "Ja, Itzehoe und der Kreis Steinburg gehören zu unserem erweiterten Einsatzgebiet. Wir sind auch hier für Sie da." },
      { q: "Wie lange dauert die Anfahrt nach Itzehoe?", a: "Nach Itzehoe benötigen wir je nach Startpunkt etwa 60-90 Minuten. Wir vereinbaren einen festen Termin, damit Sie planen können." }
    ]
  },
  "quickborn": {
    fullName: "Quickborn",
    region: "Schleswig-Holstein",
    intro: "In Quickborn und dem südlichen Kreis Pinneberg sind wir für Sie unterwegs. Professionelle Gutachten für faire Anspruchsdurchsetzung.",
    unique: "Quickborn liegt direkt an der A7 und ist damit wichtiger Knotenpunkt für den Verkehr zwischen Hamburg und dem Norden.",
    localInfo: "Die Lage an der A7 macht Quickborn zu einem Verkehrsknotenpunkt. Die Bundesstraße 4 und die Friedrichsgaber Straße sind stark befahren.",
    localFaqs: [
      { q: "Wie schnell sind Sie in Quickborn?", a: "Nach Quickborn sind wir meist innerhalb von 40-50 Minuten bei Ihnen. Die gute Autobahnanbindung macht eine schnelle Anfahrt möglich." },
      { q: "Begutachten Sie auch Fahrzeuge an der Autobahnraststätte?", a: "Ja, wenn Ihr Fahrzeug nach einem Autobahnunfall an einer Raststätte steht, können wir es auch dort begutachten." }
    ]
  },
  "wedel": {
    fullName: "Wedel",
    region: "Schleswig-Holstein",
    intro: "In Wedel an der Elbe sind wir Ihr Kfz-Sachverständiger. Die Nähe zu Hamburg und die Elbfähre sorgen für besonderes Verkehrsaufkommen.",
    unique: "Wedel als Grenzstadt zu Hamburg vereint schleswig-holsteinische Beschaulichkeit mit Hamburger Pendlerverkehr.",
    localInfo: "Die Bundesstraße 431 führt durch Wedel und verbindet die Stadt mit Hamburg-Blankenese. Die Elbfähre nach Lühe bringt zusätzlichen Verkehr.",
    localFaqs: [
      { q: "Sind Sie auch in Wedel tätig?", a: "Ja, Wedel liegt günstig an der Grenze zu Hamburg-Altona und gehört zu unserem regelmäßigen Einsatzgebiet." },
      { q: "Wie verhält es sich mit Unfällen an der Fähre?", a: "Unfälle im Bereich der Elbfähre begutachten wir genauso professionell wie alle anderen. Die besonderen Umstände dokumentieren wir entsprechend." }
    ]
  },
  "schenefeld": {
    fullName: "Schenefeld",
    region: "Schleswig-Holstein",
    intro: "In Schenefeld direkt an der Hamburger Stadtgrenze sind wir schnell bei Ihnen. Professionelle Gutachten für alle Fahrzeugschäden.",
    unique: "Schenefeld liegt unmittelbar an der Hamburger Stadtgrenze und profitiert von der guten Verkehrsanbindung.",
    localInfo: "Das Stadtzentrum mit dem großen Einkaufszentrum und die Nähe zu Hamburg-Altona sorgen für reges Verkehrsaufkommen. Die Bundesstraße 431 führt mitten durch die Stadt.",
    localFaqs: [
      { q: "Wie schnell können Sie in Schenefeld sein?", a: "Nach Schenefeld sind wir oft innerhalb von 30-40 Minuten bei Ihnen. Die Nähe zu Hamburg macht eine schnelle Anfahrt möglich." },
      { q: "Begutachten Sie auch Unfälle am Stadtzentrum?", a: "Ja, das Stadtzentrum Schenefeld kennen wir gut. Parkplatzunfälle und Kollisionen im Bereich des Einkaufszentrums sind uns vertraut." }
    ]
  },
  "halstenbek": {
    fullName: "Halstenbek",
    region: "Schleswig-Holstein",
    intro: "In Halstenbek und Umgebung sind wir Ihr kompetenter Kfz-Gutachter. Schnelle Terminvergabe, professionelle Abwicklung.",
    unique: "Halstenbek als Vorort Hamburgs verzeichnet entsprechend viel Pendlerverkehr auf den Verbindungsstraßen.",
    localInfo: "Die Lage zwischen Hamburg-Eidelstedt und Pinneberg macht Halstenbek zu einem Durchgangsort für viele Pendler. Die Bundesstraße 431 ist entsprechend stark befahren.",
    localFaqs: [
      { q: "Kommen Sie auch nach Halstenbek?", a: "Ja, Halstenbek liegt verkehrsgünstig und gehört zu unserem regelmäßigen Einsatzgebiet." },
      { q: "Wie sieht es mit der Verkehrssituation auf der B431 aus?", a: "Die B431 ist uns bestens bekannt. Wir haben hier bereits viele Unfälle dokumentiert und kennen die typischen Schadensmuster." }
    ]
  },
  "tornesch": {
    fullName: "Tornesch",
    region: "Schleswig-Holstein",
    intro: "In Tornesch sind wir für Sie da. Als wachsende Stadt im Hamburger Umland steigt auch der Bedarf an professionellen Kfz-Gutachten.",
    unique: "Tornesch wächst als Wohnstandort im Hamburger Umland kontinuierlich. Entsprechend nimmt auch das Verkehrsaufkommen zu.",
    localInfo: "Die Bundesstraße 431 und die Bahnlinie verbinden Tornesch mit Hamburg und Elmshorn. Die Stadt wächst, neue Wohngebiete entstehen.",
    localFaqs: [
      { q: "Sind Sie auch in Tornesch tätig?", a: "Ja, Tornesch gehört zu unserem Einsatzgebiet im Kreis Pinneberg. Wir sind meist innerhalb einer Stunde bei Ihnen." },
      { q: "Wie entwickelt sich das Unfallgeschehen in Tornesch?", a: "Mit dem Wachstum der Stadt nimmt auch der Verkehr zu. Wir beobachten einen Anstieg der Gutachten-Anfragen aus Tornesch." }
    ]
  },
  "uetersen": {
    fullName: "Uetersen",
    region: "Schleswig-Holstein",
    intro: "In Uetersen, der Rosenstadt, sind wir Ihr erfahrener Kfz-Sachverständiger für alle Gutachten rund um Ihr Fahrzeug.",
    unique: "Uetersen als historische Rosenstadt liegt verkehrsgünstig zwischen Hamburg und Elmshorn.",
    localInfo: "Die Rosenstadt Uetersen ist bekannt für ihr Rosarium. Verkehrstechnisch liegt sie an der Bundesstraße 431 zwischen Hamburg und Elmshorn.",
    localFaqs: [
      { q: "Kommen Sie auch nach Uetersen?", a: "Ja, Uetersen liegt im Kreis Pinneberg und gehört zu unserem Einsatzgebiet. Wir sind gerne für Sie da." },
      { q: "Wie lange dauert die Anfahrt nach Uetersen?", a: "Nach Uetersen benötigen wir etwa 50-60 Minuten. Wir vereinbaren einen festen Termin, damit Sie planen können." }
    ]
  }
};

// Default content for areas not specifically defined
const defaultAreaContent = (slug, name) => ({
  fullName: name || slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' '),
  region: "Region",
  intro: `Als Ihr Kfz-Gutachter in ${name || slug.replace(/-/g, ' ')} sind wir schnell bei Ihnen vor Ort. Professionelle Gutachten für faire Entschädigung.`,
  unique: "Wir kennen die lokalen Gegebenheiten und sind innerhalb von 60 Minuten bei Ihnen.",
  localInfo: "Ob auf der Landstraße, in der Stadt oder auf der Autobahn – wir begutachten Ihr Fahrzeug dort, wo es für Sie am besten passt.",
  localFaqs: [
    { q: "Wie schnell sind Sie vor Ort?", a: "Wir sind in der Regel innerhalb von 60 Minuten bei Ihnen. Bei dringenden Fällen versuchen wir, noch schneller zu sein." },
    { q: "Was kostet das Gutachten?", a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Versicherung die Kosten. Sie zahlen nichts." }
  ]
});

const EinsatzgebietDetailPage = () => {
  const { slug } = useParams();
  const [serviceAreas, setServiceAreas] = useState([]);
  
  useEffect(() => {
    axios.get(`${API}/service-areas`).then(res => setServiceAreas(res.data)).catch(console.error);
    window.scrollTo(0, 0);
  }, [slug]);

  const area = areaContent[slug] || defaultAreaContent(slug);

  const services = [
    { icon: Car, title: "Unfallgutachten", desc: "Nach einem Unfall schnell vor Ort", link: "/leistungen/unfallgutachten" },
    { icon: Shield, title: "Haftpflichtgutachten", desc: "Kostenübernahme durch Gegner", link: "/leistungen/haftpflichtgutachten" },
    { icon: FileText, title: "Wertgutachten", desc: "Fahrzeugwert professionell ermittelt", link: "/leistungen/wertgutachten" }
  ];

  // Advantages over insurance adjusters
  const advantages = [
    {
      icon: Scale,
      title: "Unabhängigkeit",
      yours: "Wir arbeiten ausschließlich in Ihrem Interesse und sind an keine Versicherung gebunden.",
      theirs: "Der Versicherungsgutachter wird von der gegnerischen Versicherung bezahlt und hat ein Interesse, den Schaden gering zu halten."
    },
    {
      icon: ThumbsUp,
      title: "Vollständige Schadenserfassung",
      yours: "Wir dokumentieren jeden Schaden – auch versteckte Mängel und Wertminderung.",
      theirs: "Versicherungsgutachter übersehen oft Schäden oder bewerten sie zu niedrig."
    },
    {
      icon: Users,
      title: "Ihre freie Wahl",
      yours: "Sie haben das Recht auf einen eigenen Gutachter – die Kosten trägt die Gegenseite.",
      theirs: "Die Versicherung schickt ihren eigenen Gutachter, um Kosten zu sparen."
    },
    {
      icon: BadgeCheck,
      title: "Erfahrung & Qualifikation",
      yours: "Kfz-Meister, unabhängig zertifiziert.",
      theirs: "Oft weniger qualifiziert oder unter Zeitdruck arbeitend."
    }
  ];

  // General FAQs about why an independent appraiser is important
  const generalFaqs = [
    {
      q: "Warum sollte ich nicht den Gutachter der Versicherung nehmen?",
      a: "Der Versicherungsgutachter wird von der gegnerischen Versicherung beauftragt und bezahlt. Sein Interesse liegt darin, den Schaden möglichst gering zu bewerten, um Kosten für die Versicherung zu sparen. Ein unabhängiger Gutachter hingegen arbeitet ausschließlich in Ihrem Interesse und dokumentiert den Schaden vollständig."
    },
    {
      q: "Was sind die häufigsten Fehler bei Versicherungsgutachten?",
      a: "Versicherungsgutachter unterschlagen häufig die merkantile Wertminderung, bewerten Reparaturkosten zu niedrig, übersehen versteckte Schäden und setzen den Restwert zu hoch an. All das führt dazu, dass Sie weniger Geld erhalten, als Ihnen zusteht."
    },
    {
      q: "Wer trägt die Kosten für ein unabhängiges Gutachten?",
      a: "Bei einem unverschuldeten Unfall übernimmt die Haftpflichtversicherung des Unfallverursachers die Kosten für das Gutachten. Sie müssen nichts bezahlen und haben trotzdem einen Gutachter, der in Ihrem Interesse arbeitet."
    },
    {
      q: "Was ist, wenn die Versicherung mein Gutachten nicht akzeptiert?",
      a: "Ein professionelles, unabhängiges Gutachten ist rechtlich bindend. Die Versicherung muss es akzeptieren. Sollte es dennoch zu Unstimmigkeiten kommen, unterstützen wir Sie bei der Durchsetzung Ihrer Ansprüche."
    },
    {
      q: "Kann ich den Gutachter wirklich frei wählen?",
      a: "Ja! Nach § 249 BGB haben Sie bei einem unverschuldeten Unfall das Recht, einen eigenen Sachverständigen zu beauftragen. Die Kosten sind Teil des erstattungsfähigen Schadens und werden von der gegnerischen Versicherung übernommen."
    }
  ];

  return (
    <>
      <SEO 
        title={`Kfz Gutachter ${area.fullName}`}
        description={`Ihr unabhängiger Kfz-Gutachter in ${area.fullName}. Unfallgutachten, Wertgutachten & Schadensdokumentation. Innerhalb 60 Min. vor Ort. Kostenlos bei unverschuldetem Unfall.`}
        canonical={`/einsatzgebiete/${slug}`}
        keywords={`Kfz Gutachter ${area.fullName}, Unfallgutachten ${area.fullName}, Sachverständiger ${area.region}`}
      />
      {/* Breadcrumb */}
      <div className="bg-[#F7F7F7] py-4 border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-[#4A4A4A] hover:text-[#AA6622]">Startseite</Link>
            <span className="text-[#4A4A4A]">/</span>
            <Link to="/einsatzgebiete" className="text-[#4A4A4A] hover:text-[#AA6622]">Einsatzgebiete</Link>
            <span className="text-[#4A4A4A]">/</span>
            <span className="text-[#AA6622] font-medium">{area.fullName}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#F7F7F7] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/einsatzgebiete" className="inline-flex items-center text-[#AA6622] font-medium mb-6 hover:text-[#8e541b]">
            <ArrowLeft className="w-4 h-4 mr-2" />Alle Einsatzgebiete
          </Link>
          <div className="flex items-center space-x-3 mb-4">
            <MapPin className="w-8 h-8 text-[#AA6622]" />
            <span className="text-[#AA6622] font-medium">{area.region}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-black mb-6 font-['Outfit']">
            Kfz Gutachter in {area.fullName}
          </h1>
          <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8 max-w-3xl">{area.intro}</p>
          <div className="flex flex-wrap gap-4 mb-8">
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-sm">
              <Clock className="w-5 h-5 text-[#AA6622]" />
              <span className="font-medium">Innerhalb 60 Min. vor Ort</span>
            </div>
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-sm">
              <Star className="w-5 h-5 text-[#AA6622]" />
              <span className="font-medium">5.0 bei Google</span>
            </div>
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-sm">
              <BadgeCheck className="w-5 h-5 text-[#AA6622]" />
              <span className="font-medium">Unabhängig & zertifiziert</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>Jetzt anrufen</span>
            </a>
            <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="border-2 border-black text-black px-6 py-3 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-black hover:text-white transition-colors">
              <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              {/* Local Info */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-black mb-4 font-['Outfit']">Ihr Kfz-Sachverständiger in {area.fullName}</h2>
                <p className="text-[#4A4A4A] leading-relaxed mb-6">{area.unique}</p>
                <p className="text-[#4A4A4A] leading-relaxed mb-6">{area.localInfo}</p>
                <p className="text-[#4A4A4A] leading-relaxed">
                  Als unabhängiger Kfz-Gutachter arbeiten wir ausschließlich in Ihrem Interesse – nicht im Interesse einer Versicherung. Wir dokumentieren jeden Schaden vollständig und sorgen dafür, dass Sie die Entschädigung erhalten, die Ihnen zusteht.
                </p>
              </div>

              {/* Why Independent Appraiser */}
              <div className="mb-16">
                <div className="flex items-center space-x-3 mb-6">
                  <AlertTriangle className="w-8 h-8 text-[#AA6622]" />
                  <h2 className="text-2xl font-bold text-black font-['Outfit']">Warum ein unabhängiges Gutachten wichtig ist</h2>
                </div>
                <div className="bg-[#FFF8F0] border-l-4 border-[#AA6622] p-6 rounded-r-sm mb-6">
                  <p className="text-[#4A4A4A] leading-relaxed">
                    <strong className="text-black">Wussten Sie?</strong> Bei einem unverschuldeten Unfall haben Sie das Recht auf einen eigenen, unabhängigen Gutachter. Die Kosten übernimmt die gegnerische Versicherung. Trotzdem versuchen viele Versicherungen, Ihnen einen eigenen Gutachter "anzubieten" – der natürlich im Interesse der Versicherung arbeitet, nicht in Ihrem.
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#AA6622] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Vollständige Schadenserfassung</h4>
                      <p className="text-[#4A4A4A]">Wir dokumentieren jeden Schaden – auch versteckte Mängel, die erst später Probleme verursachen könnten.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#AA6622] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Wertminderung nicht vergessen</h4>
                      <p className="text-[#4A4A4A]">Ihr Fahrzeug verliert nach einem Unfall an Wert – auch nach der Reparatur. Wir berechnen diese Wertminderung.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#AA6622] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Realistische Restwertermittlung</h4>
                      <p className="text-[#4A4A4A]">Versicherungen setzen den Restwert oft zu hoch an. Wir ermitteln den tatsächlichen Marktwert.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-8 h-8 bg-[#AA6622] rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-black mb-1">Rechtssichere Dokumentation</h4>
                      <p className="text-[#4A4A4A]">Unser Gutachten ist rechtlich bindend und hält auch einer gerichtlichen Überprüfung stand.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Table */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-black mb-6 font-['Outfit']">Unabhängiger Gutachter vs. Versicherungsgutachter</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left p-4 bg-[#F7F7F7] font-bold text-black">Kriterium</th>
                        <th className="text-left p-4 bg-[#AA6622] text-white font-bold">Unabhängiger Gutachter</th>
                        <th className="text-left p-4 bg-gray-300 text-black font-bold">Versicherungsgutachter</th>
                      </tr>
                    </thead>
                    <tbody>
                      {advantages.map((adv, i) => (
                        <tr key={i} className="border-b border-[#E5E5E5]">
                          <td className="p-4 font-medium text-black">
                            <div className="flex items-center space-x-2">
                              <adv.icon className="w-5 h-5 text-[#AA6622]" />
                              <span>{adv.title}</span>
                            </div>
                          </td>
                          <td className="p-4 bg-[#FFF8F0]">
                            <div className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-[#4A4A4A]">{adv.yours}</span>
                            </div>
                          </td>
                          <td className="p-4 bg-gray-50">
                            <div className="flex items-start space-x-2">
                              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                              <span className="text-[#4A4A4A]">{adv.theirs}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Our Services */}
              <div className="mb-16">
                <h3 className="text-xl font-bold text-black mb-6 font-['Outfit']">Unsere Leistungen in {area.fullName}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {services.map((s, i) => (
                    <Link key={i} to={s.link} className="bg-[#F7F7F7] p-6 rounded-sm hover:shadow-md transition-shadow group">
                      <s.icon className="w-10 h-10 text-[#AA6622] mb-4 group-hover:scale-110 transition-transform" />
                      <h4 className="font-bold text-black mb-2">{s.title}</h4>
                      <p className="text-sm text-[#4A4A4A]">{s.desc}</p>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Process */}
              <div className="mb-16">
                <h3 className="text-xl font-bold text-black mb-6 font-['Outfit']">So läuft die Begutachtung in {area.fullName} ab</h3>
                <ol className="space-y-6">
                  <li className="flex items-start space-x-4">
                    <span className="w-10 h-10 bg-[#AA6622] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">1</span>
                    <div>
                      <h4 className="font-bold text-black text-lg">Kontakt aufnehmen</h4>
                      <p className="text-[#4A4A4A]">Rufen Sie uns an oder schreiben Sie per WhatsApp – wir sind täglich von 9:00 bis 23:00 Uhr erreichbar. Schildern Sie kurz Ihren Fall.</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <span className="w-10 h-10 bg-[#AA6622] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">2</span>
                    <div>
                      <h4 className="font-bold text-black text-lg">Wir kommen zu Ihnen nach {area.fullName}</h4>
                      <p className="text-[#4A4A4A]">Innerhalb von 60 Minuten sind wir bei Ihnen vor Ort – am Unfallort, bei Ihnen zu Hause, am Arbeitsplatz oder in der Werkstatt.</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <span className="w-10 h-10 bg-[#AA6622] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">3</span>
                    <div>
                      <h4 className="font-bold text-black text-lg">Professionelle Begutachtung</h4>
                      <p className="text-[#4A4A4A]">Wir dokumentieren jeden Schaden mit Fotos und technischen Messungen. Die Begutachtung dauert etwa 30-45 Minuten.</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <span className="w-10 h-10 bg-[#AA6622] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">4</span>
                    <div>
                      <h4 className="font-bold text-black text-lg">Gutachten in 24 Stunden</h4>
                      <p className="text-[#4A4A4A]">Innerhalb von 24 Stunden erhalten Sie Ihr vollständiges, rechtssicheres Gutachten per E-Mail.</p>
                    </div>
                  </li>
                  <li className="flex items-start space-x-4">
                    <span className="w-10 h-10 bg-[#AA6622] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">5</span>
                    <div>
                      <h4 className="font-bold text-black text-lg">Wir regeln den Rest</h4>
                      <p className="text-[#4A4A4A]">Auf Wunsch übernehmen wir die Kommunikation mit der Versicherung und setzen Ihre Ansprüche durch.</p>
                    </div>
                  </li>
                </ol>
              </div>

              {/* General FAQ */}
              <div className="mb-16">
                <h2 className="text-2xl font-bold text-black mb-6 font-['Outfit']">Häufige Fragen zum unabhängigen Gutachter</h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {generalFaqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`general-${index}`}
                      className="bg-[#F7F7F7] rounded-sm border-none"
                    >
                      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-100 font-bold text-left text-black">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4 text-[#4A4A4A] leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Local FAQ */}
              {area.localFaqs && area.localFaqs.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-black mb-6 font-['Outfit']">Häufige Fragen zu {area.fullName}</h2>
                  <Accordion type="single" collapsible className="space-y-3">
                    {area.localFaqs.map((faq, index) => (
                      <AccordionItem
                        key={index}
                        value={`local-${index}`}
                        className="bg-[#F7F7F7] rounded-sm border-none"
                      >
                        <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-100 font-bold text-left text-black">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4 text-[#4A4A4A] leading-relaxed">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div>
              <div className="bg-[#F7F7F7] p-6 rounded-sm sticky top-24">
                <h3 className="text-xl font-bold text-black mb-4 font-['Outfit']">Jetzt Termin vereinbaren</h3>
                <p className="text-[#4A4A4A] mb-6">Wir sind täglich von 9:00 bis 23:00 Uhr für Sie erreichbar – auch am Wochenende.</p>
                <div className="space-y-4">
                  <a href="tel:+491748936514" className="w-full bg-[#AA6622] text-white px-6 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
                    <Phone className="w-5 h-5" /><span>0174 893 6514</span>
                  </a>
                  <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="w-full bg-[#25D366] text-white px-6 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors">
                    <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
                  </a>
                  <Link to="/kontakt" className="w-full border-2 border-black text-black px-6 py-4 rounded-sm font-bold flex items-center justify-center hover:bg-black hover:text-white transition-colors">
                    Kontaktformular
                  </Link>
                </div>
                <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
                  <div className="flex items-center space-x-2 mb-2">
                    <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-[#AA6622] text-[#AA6622]" />)}</div>
                    <span className="font-bold">5.0</span>
                  </div>
                  <p className="text-sm text-[#4A4A4A]">222 zufriedene Kunden in Hamburg & Schleswig-Holstein</p>
                </div>

                {/* Trust Elements */}
                <div className="mt-6 pt-6 border-t border-[#E5E5E5]">
                  <h4 className="font-bold text-black mb-4">Ihre Vorteile:</h4>
                  <ul className="space-y-3">
                    <li className="flex items-center space-x-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#AA6622]" />
                      <span>Kostenlos bei unverschuldetem Unfall</span>
                    </li>
                    <li className="flex items-center space-x-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#AA6622]" />
                      <span>Innerhalb 60 Min. vor Ort</span>
                    </li>
                    <li className="flex items-center space-x-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#AA6622]" />
                      <span>Gutachten in 24 Stunden</span>
                    </li>
                    <li className="flex items-center space-x-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#AA6622]" />
                      <span>Unabhängig & objektiv</span>
                    </li>
                    <li className="flex items-center space-x-2 text-sm text-[#4A4A4A]">
                      <CheckCircle className="w-4 h-4 text-[#AA6622]" />
                      <span>Kfz-Meister & Zertifiziert</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Areas */}
      {area.districts && (
        <section className="py-16 bg-[#F7F7F7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-black mb-8 font-['Outfit']">Weitere Einsatzgebiete in {area.region}</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {area.districts.map((d, i) => (
                <Link key={i} to={`/einsatzgebiete/${d.toLowerCase().replace(/\s/g, '-').replace(/ü/g, 'ue').replace(/ä/g, 'ae').replace(/ö/g, 'oe')}`} className="bg-white p-4 rounded-sm hover:bg-[#AA6622] hover:text-white transition-colors group">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-[#AA6622] group-hover:text-white" />
                    <span className="font-medium">{d}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-black mb-6 font-['Outfit']">Unfall in {area.fullName}? Wir helfen sofort!</h2>
          <p className="text-lg text-[#4A4A4A] mb-8">
            Kontaktieren Sie uns jetzt für eine kostenlose Erstberatung. Bei einem unverschuldeten Unfall zahlen Sie nichts – die Kosten übernimmt die gegnerische Versicherung.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:+491748936514" className="bg-[#AA6622] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#8e541b] transition-colors">
              <Phone className="w-5 h-5" /><span>0174 893 6514</span>
            </a>
            <a href="https://wa.me/+491748936514" target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-4 rounded-sm font-bold flex items-center justify-center space-x-2 hover:bg-[#20bd5a] transition-colors">
              <MessageCircle className="w-5 h-5" /><span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default EinsatzgebietDetailPage;
